<template>
  <div class="app">
    <div class="app__content" :class="{'show-nav': !this.$isMobile}">
      <div class="app__content__nav" v-show="!$isMobile && !isLogin">
        <smd-one-col-layout ref="navigation"
                            class="full-width">
          <header slot="header">
            <div class="smd-brand"
                 :style="brandStyles">
            </div>
          </header>
          <nav :class="{ hide: onChatWindow }">
            <div class="menu">
              <router-link v-for="(menuLink, index) in navItems"
                           :key="index"
                           :to="menuLink.to"
                           class="menu-link">
                <span class="menu-link__icon"
                      :class="[computedActiveIcon(menuLink), {'custom-icon-size': menuLink.customSize}]">
                <template v-if="menuLink.paths">
                  <span v-for="pathNumber in menuLink.paths"
                        :class="`path${pathNumber}`"
                        :key="pathNumber"></span>
                </template>
                   <smd-notification-badge :notifs="menuLink.notifs"/>
                </span>
                <div class="menu-link__name Label1 gray-400">
                  {{ menuLink.name }}
                  <smd-notification-badge :notifs="menuLink.notifs"/>
                </div>
              </router-link>
            </div>
          </nav>
        </smd-one-col-layout>
      </div>
      <div class="app__content__page">
        <router-view/>
      </div>
    </div>
    <smd-modal/>
    <smd-alert/>
    <toast/>
    <otp-verification-modal v-if="showOTP"
                            v-bind="otpModalProps"
                            @verify-success="onVerifySuccess"/>
    <terms-and-agreement v-if="showTos"/>
  </div>
</template>

<script>
  import {bus} from '@/main';
  import {logout} from "@/utils/logout";
  import {mapGetters} from 'vuex';

  import Logo from '@/assets/images/ico029.svg';
  import LogoSmall from '@/assets/images/now-serving-heart.svg';
  import {navDesktop} from '@/reference/nav-items';
  import OtpVerificationModal from '@/components/otp-verification-modal';
  import Routes from "@/router/routes";
  import TermsAndAgreement from '@/components/TermsAndAgreement';
  import Toast from '@/components/toast';

  import {
    SHOW_OTP_EVENT,
    CLOSE_OTP_EVENT,
    OTP_SUCCESS_EVENT,
    SHOW_TOS,
    CLOSE_TOS,
  } from '@/constants/bus';

  import {
    GETTER_TOAST_STATE,
    GETTER_UNREAD_MESSAGES,
    ROUTING_SHOW_OVERLAY
  } from '@/store/types';

  export default {
    name: 'App',
    components: {
      OtpVerificationModal,
      TermsAndAgreement,
      Toast,
    },
    data() {
      return {
        showOTP: false,
        otpModalProps: {},
        showTos: false,
      };
    },
    computed: {
      ...mapGetters({
        toastState: GETTER_TOAST_STATE,
        showOverlay: ROUTING_SHOW_OVERLAY,
        unreadCountsList: GETTER_UNREAD_MESSAGES,
      }),
      brandStyles() {
        return `backgroundImage: url(${this.$isDesktop ? Logo : LogoSmall})`;
      },
      isLogin() {
        return this.$route.name === Routes.LOGIN_ROUTE;
      },
      onChatWindow() {
        return [
          Routes.CHAT_ROUTE,
          Routes.ORDER_CHAT_ROUTE,
        ].includes(this.$route.name);
      },
      navItems() {
        const hasUnreadMessage = Object.values(this.unreadCountsList).some((unreadCount) => unreadCount > 0);
        const navItems = [...navDesktop];

        if (hasUnreadMessage) {
          const messageTabIndex = navItems.findIndex(({id}) => id === Routes.MESSAGES_ROUTE);
          const notifs = Object.values(this.unreadCountsList).filter((noOfUnread) => !!noOfUnread).length;
          this.$set(navItems, messageTabIndex, {...navItems[messageTabIndex], notifs});
        }

        return navItems;
      },
    },
    created() {
      bus.$on(CLOSE_OTP_EVENT, () => {
        this.showOTP = false;
        this.otpModalProps = {};
      });

      bus.$on(CLOSE_TOS, () => {
        this.showTos = false;
      });

      bus.$on(SHOW_OTP_EVENT, (otpModalProps) => {
        this.showOTP = true;
        this.otpModalProps = otpModalProps;
      });

      bus.$on(SHOW_TOS, () => {
        this.showTos = true;
      });
    },
    methods: {
      computedActiveIcon(item) {
        const name = item.to.name;
        const activeRoute = this.$route.meta.root || this.$route.name;
        return activeRoute === name ? item.iconSelected : item.icon;
      },
      onVerifySuccess(accessToken) {
        bus.$emit(OTP_SUCCESS_EVENT, accessToken);
      },
      logout,
    },
  }
</script>
<style lang="scss">
  $navDesktopWidth: 160px;

  .app {
    .show-nav {
      .app__content__nav {
        width: $navDesktopWidth;

        .smd-brand {
          height: 26px;
          width: 140px;
        }
      }
    }
  }

  nav {
    height: 100%;
    padding: 0 5px 10px;
    display: flex;
    flex-direction: column;
    background-color: color(base, background);

    .logo {
      background: url('~@/assets/images/now-serving-logo-horizontal.svg') no-repeat center;
      background-size: contain;
      height: 40px;
      margin-bottom: 8px;

      @include responsive(tablet) {
        display: none;
      }
    }

    .menu {
      flex: 1;
      display: flex;
      flex-direction: column;

      .menu-link {
        @extend %layout--flex-row-center-x;
        padding: 12px 15px;
        color: inherit;
        border-radius: radius(normal);

        @include responsive('max-width: 1023px') {
          justify-content: center;
        }


        &:not(:last-child) {
          margin-bottom: space(s);
        }

        > [class^="ns-icon-"] {
          @include responsive('min-width: 1024px') {
            font-size: 36px;
            margin-right: space(s);
          }
          font-size: 27px;
        }

        .custom-icon-size {
          color: color(gray-400);
          padding: space(xs)+1;
          font-size: 17px;
          transition: color 0.1s;
        }

        &:hover {
          background-color: color(dark, background);
        }

        &.router-link-active {
          color: color(purple);
          background-color: color(dark, background);

          .custom-icon-size {
            color: color(purple);
          }
        }

        .logout-icon {
          font-size: 16px;
          padding-left: space(xs);
          margin-right: space(xs)+2;
        }
      }

      .menu-link__icon {
        @include responsive('max-width: 1023px') {
          font-size: 28px;
        }
        font-size: 36px;
        position: relative;

        .smd-notification-badge {
          @include responsive('min-width: 1024px') {
            display: none;
          }
          min-height: 13px;
          min-width: 13px;
          right: -8px;
          top: -5px;
          font-size: 10px;
          font-family: D-DIN;
        }
      }

      .menu-link__name {
        @include responsive('max-width: 1023px') {
          display: none;
        }

        position: relative;
        margin-left: space(s);

        .smd-notification-badge {
          min-height: 20px;
          min-width: 20px;
          right: -30px;
        }
      }
    }
  }
</style>